import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Trash2, Upload, Link2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { API_URL, resolveMediaUrl } from "../../lib/utils";

const ACCEPT = "image/png,image/jpeg,image/webp,image/gif";
const MAX = 5 * 1024 * 1024;

export const uploadProductImage = async (productId, file, headers) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API_URL}/products/${productId}/image`, fd, { headers, withCredentials: true });
  return res.data.image_url;
};

export const ProductImageField = ({ productId, imageUrl, onChange, pendingFile, onPendingFile, headers }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const preview = pendingFile ? URL.createObjectURL(pendingFile) : resolveMediaUrl(imageUrl);

  const handleFile = async (file) => {
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type)) return toast.error("Formato inválido. Use PNG, JPG, WEBP ou GIF.");
    if (file.size > MAX) return toast.error("Imagem muito grande. Máximo 5MB.");
    if (!productId) {
      onPendingFile(file);
      return;
    }
    setUploading(true);
    try {
      const url = await uploadProductImage(productId, file, headers);
      onChange(url);
      toast.success("Foto carregada!");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao carregar a foto");
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (pendingFile) return onPendingFile(null);
    if (productId && imageUrl?.startsWith("/api/files/")) {
      try {
        await axios.delete(`${API_URL}/products/${productId}/image`, { headers, withCredentials: true });
      } catch (e) {
        return toast.error("Erro ao remover a foto");
      }
    }
    onChange("");
  };

  return (
    <div className="space-y-2" data-testid="product-image-field">
      <Label>Foto do Artigo</Label>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
          className={`relative w-32 h-24 shrink-0 rounded-lg border-2 border-dashed overflow-hidden flex items-center justify-center bg-muted/40 transition-colors ${
            dragging ? "border-emerald-500 bg-emerald-500/10" : "border-border hover:border-emerald-500/60"
          }`}
          data-testid="product-image-dropzone"
        >
          {preview ? (
            <img src={preview} alt="Pré-visualização" className="w-full h-full object-cover" data-testid="product-image-preview" />
          ) : (
            <span className="flex flex-col items-center gap-1 text-[11px] text-muted-foreground">
              <ImagePlus className="w-5 h-5" /> Sem foto
            </span>
          )}
          {uploading && (
            <span className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </span>
          )}
        </button>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} className="gap-1.5" data-testid="product-image-upload-btn">
              <Upload className="w-3.5 h-3.5" /> {preview ? "Trocar foto" : "Carregar foto"}
            </Button>
            {preview && (
              <Button type="button" variant="ghost" size="sm" onClick={remove} className="gap-1.5 text-red-500 hover:text-red-600" data-testid="product-image-remove-btn">
                <Trash2 className="w-3.5 h-3.5" /> Remover
              </Button>
            )}
          </div>
          <div className="relative">
            <Link2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={imageUrl?.startsWith("/api/files/") ? "" : imageUrl || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder={imageUrl?.startsWith("/api/files/") ? "Foto carregada do dispositivo" : "ou cole um URL https://..."}
              className="pl-8 h-8 text-xs"
              data-testid="product-image-url-input"
            />
          </div>
          <p className="text-[11px] text-muted-foreground">PNG, JPG, WEBP ou GIF · máx. 5MB{pendingFile ? " · a foto será carregada ao guardar" : ""}</p>
        </div>
      </div>
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="product-image-file-input" />
    </div>
  );
};
